import { KeyboardEvent as ReactKeyboardEvent, ReactNode, useId, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optional adornment rendered before the label, in both the list and the trigger. */
  icon?: ReactNode;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

/**
 * Single-select dropdown with typeahead, composed from shadcn `Popover` +
 * `Command` (the repo's Radix-based combobox pattern). The `Command` primitive
 * does no internal filtering, so options are filtered here on the typed text.
 */
const Combobox = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled = false,
  loading = false,
  className,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const optionIdPrefix = useId();

  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel = selectedOption?.label;
  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
    : options;

  // Clamped rather than reset in an effect: typing can shorten the list under a
  // highlight that has already moved past its new end.
  const activeIndex = Math.min(highlighted, filteredOptions.length - 1);
  const activeOption = filteredOptions[activeIndex];

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    setHighlighted(0);
    if (!next) {
      setSearch("");
    }
  };

  const handleSearchChange = (next: string) => {
    setSearch(next);
    setHighlighted(0);
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
    setSearch("");
    setHighlighted(0);
  };

  // Arrow keys move the highlight through the filtered list and Enter takes it, so
  // narrowing by typing and choosing never needs the mouse. Wraps at both ends.
  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (filteredOptions.length === 0) {
      return;
    }
    const lastIndex = filteredOptions.length - 1;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlighted(activeIndex >= lastIndex ? 0 : activeIndex + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlighted(activeIndex <= 0 ? lastIndex : activeIndex - 1);
        break;
      case "Home":
        event.preventDefault();
        setHighlighted(0);
        break;
      case "End":
        event.preventDefault();
        setHighlighted(lastIndex);
        break;
      case "Enter":
        if (activeOption) {
          event.preventDefault();
          handleSelect(activeOption.value);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            "w-full justify-between font-normal",
            !selectedLabel && "text-muted-foreground",
            className
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {!loading && selectedOption?.icon}
            <span className="truncate">
              {loading ? "Loading..." : (selectedLabel ?? placeholder)}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[1300] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        // Stop wheel/touch from reaching the scroll-lock a parent Dialog installs
        // (react-remove-scroll), which would otherwise block scrolling this list.
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            aria-activedescendant={activeOption ? `${optionIdPrefix}-${activeIndex}` : undefined}
          />
          <CommandList role="listbox">
            {filteredOptions.length === 0 ? (
              <CommandEmpty>{emptyText}</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredOptions.map((option, index) => (
                  <CommandItem
                    key={option.value}
                    id={`${optionIdPrefix}-${index}`}
                    value={option.value}
                    onSelect={handleSelect}
                    aria-selected={index === activeIndex}
                    // Keeps the keyboard highlight inside the scrolling list. Only the
                    // active item gets the callback, so arrowing past the fold scrolls
                    // but hovering with the mouse doesn't move the viewport.
                    ref={
                      index === activeIndex
                        ? (element) => element?.scrollIntoView({ block: "nearest" })
                        : undefined
                    }
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.icon && <span className="mr-2 shrink-0">{option.icon}</span>}
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
